import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { Upload, Image as ImageIcon, Gift, Trophy, Play, ArrowLeft, Trash2, Eye, EyeOff, ImageDown, ClipboardPaste, Eraser, Wand2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { WinnerCardModal } from "@/components/admin/WinnerCard";

const FILLER_A = ["Tetra", "Guppy", "Pleco", "Betta", "Molly", "Neon", "Corydoras", "Danio", "Gourami", "Rasbora", "Angelfish", "Discus", "Killifish", "Shrimp", "Oscar", "Platy", "Loach", "Barb"];
const FILLER_B = ["Keeper", "Tank", "Fan", "Lover", "Room", "Hobby", "Dad", "Mum", "Guy", "Girl", "Breeder", "Club", "Life", "World", "Scape", "Haven"];
const SLICE_COLORS = ["#0E2A38", "#14556A", "#0B3A4A", "#1C7A93", "#0E2A38", "#25C4D8"];
const polar = (cx, cy, r, deg) => {
  const a = (deg - 90) * (Math.PI / 180);
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};
const slicePath = (cx, cy, r, start, end) => {
  const [x1, y1] = polar(cx, cy, r, start);
  const [x2, y2] = polar(cx, cy, r, end);
  const large = end - start > 180 ? 1 : 0;
  return `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} Z`;
};

const Wheel = ({ entrants, rotation, spinning }) => {
  const n = entrants.length;
  const seg = n > 0 ? 360 / n : 360;
  const cx = 260, cy = 260, r = 250;
  const fontSize = n <= 12 ? 15 : n <= 24 ? 12 : n <= 45 ? 9 : n <= 70 ? 7 : 6;
  return (
    <div className="relative select-none" style={{ width: 520, maxWidth: "92vw" }}>
      {/* pointer */}
      <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-20" style={{ filter: "drop-shadow(0 2px 3px rgba(0,0,0,.5))" }}>
        <div style={{ width: 0, height: 0, borderLeft: "18px solid transparent", borderRight: "18px solid transparent", borderTop: "30px solid #25C4D8" }} />
      </div>
      <svg viewBox="0 0 520 520" className="w-full h-auto rounded-full" style={{ boxShadow: "0 0 0 10px #0C131D, 0 0 40px rgba(37,196,216,.25)" }} data-testid="giveaway-wheel">
        <g style={{ transform: `rotate(${rotation}deg)`, transformOrigin: "260px 260px", transition: spinning ? "transform 6s cubic-bezier(0.15,0.85,0.15,1)" : "none" }}>
          <circle cx={cx} cy={cy} r={r} fill="#0C131D" />
          {entrants.map((name, i) => {
            const start = i * seg, end = (i + 1) * seg, mid = start + seg / 2;
            const [tx, ty] = polar(cx, cy, r * 0.62, mid);
            return (
              <g key={i}>
                <path d={slicePath(cx, cy, r, start, end)} fill={SLICE_COLORS[i % SLICE_COLORS.length]} stroke="#0C131D" strokeWidth="1" />
                <text x={tx} y={ty} fill="#EAF6F8" fontSize={fontSize} fontWeight="600"
                  textAnchor="middle" dominantBaseline="middle"
                  transform={`rotate(${mid}, ${tx}, ${ty})`}>
                  {name.length > 18 ? name.slice(0, 17) + "…" : name}
                </text>
              </g>
            );
          })}
          {n === 0 && <text x={cx} y={cy} fill="#5b7480" fontSize="16" textAnchor="middle">No entrants yet</text>}
        </g>
        <circle cx={cx} cy={cy} r={34} fill="#25C4D8" stroke="#0C131D" strokeWidth="5" />
      </svg>
    </div>
  );
};

export default function GiveawayPage() {
  const [tab, setTab] = useState("setup");
  const [g, setG] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [winners, setWinners] = useState([]);
  const [card, setCard] = useState(null);
  const [pasteText, setPasteText] = useState("");
  const rotRef = useRef(0);

  const normName = (s) => s.trim().toLowerCase().replace(/^@/, "");
  const fillers = g?.filler_names || [];
  const realEntrants = (g?.entrants || []).filter((e) => !fillers.includes(e));

  const makeFillers = (real, count) => {
    const taken = new Set(real.map(normName));
    const out = [];
    while (out.length < count) {
      const n = `@${FILLER_A[Math.floor(Math.random() * FILLER_A.length)]}${FILLER_B[Math.floor(Math.random() * FILLER_B.length)]}${Math.floor(Math.random() * 900 + 10)}`;
      if (!taken.has(normName(n))) { taken.add(normName(n)); out.push(n); }
    }
    return out;
  };

  const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; };

  const autoFill = async () => {
    const f = makeFillers(realEntrants, 100 - realEntrants.length);
    await save({ entrants: shuffle([...realEntrants, ...f]), filler_names: f });
    toast.success(`Added ${f.length} filler names — they can never win`);
  };

  const removeFillers = async () => {
    await save({ entrants: realEntrants, filler_names: [] });
    toast.success("Filler names removed");
  };

  const addPasted = async () => {
    const pasted = pasteText.split(/\n+/).map((s) => s.trim().replace(/\s+/g, " ")).filter(Boolean);
    if (!pasted.length) return;
    const seen = new Set(realEntrants.map(normName));
    const fresh = pasted.filter((n) => { const k = normName(n); if (seen.has(k)) return false; seen.add(k); return true; });
    const merged = [...realEntrants, ...fresh];
    const kept = merged.slice(0, 100);
    const keptFillers = fillers.length ? makeFillers(kept, 100 - kept.length) : [];
    try {
      await save({ entrants: keptFillers.length ? shuffle([...kept, ...keptFillers]) : kept, filler_names: keptFillers });
      setPasteText("");
      const added = Math.max(0, kept.length - realEntrants.length);
      const skipped = pasted.length - fresh.length;
      toast.success(`Added ${added} username${added === 1 ? "" : "s"}${skipped ? ` · ${skipped} duplicate${skipped === 1 ? "" : "s"} skipped` : ""}`);
      if (merged.length > 100) toast.error(`List is capped at 100 — ${merged.length - 100} not added`);
    } catch {
      toast.error("Could not add usernames");
    }
  };

  const clearEntrants = async () => {
    if (!window.confirm(`Clear all ${g.entrants.length} entrants?`)) return;
    await save({ entrants: [], filler_names: [] });
    toast.success("Entrant list cleared");
  };

  const load = () => api.get("/giveaway").then((r) => { setG(r.data); }).catch(() => {});
  const loadWinners = () => api.get("/giveaway/winners").then((r) => setWinners(r.data)).catch(() => {});
  useEffect(() => { load(); loadWinners(); }, []);

  const save = async (patch) => {
    const { data } = await api.put("/giveaway", patch);
    setG(data);
    return data;
  };

  const uploadNames = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/giveaway/upload-names", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Loaded ${data.count} names`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };

  const uploadPrize = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/giveaway/prize-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Prize image uploaded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };

  const spin = async () => {
    if (spinning) return;
    // Re-fetch live state before every spin so a change made elsewhere
    // (e.g. the Random slider toggled on another phone) is honoured immediately.
    let latest = g;
    try { const { data } = await api.get("/giveaway"); latest = data; setG(data); } catch {}
    const entrants = latest.entrants || [];
    const n = entrants.length;
    const fill = new Set(latest.filler_names || []);
    const eligible = entrants.map((e, i) => (fill.has(e) ? -1 : i)).filter((i) => i >= 0);
    if (n === 0 || eligible.length === 0) return;
    setReveal(null);
    const normU = (s) => (s || "").trim().toLowerCase().replace(/^@/, "").replace(/\s+/g, " ");
    const sw = latest.random_mode ? "" : normU(latest.secret_winner);
    let idx = sw ? entrants.findIndex((e, i) => !fill.has(e) && normU(e) === sw) : -1;
    if (idx < 0) idx = eligible[Math.floor(Math.random() * eligible.length)];
    const seg = 360 / n;
    const center = idx * seg + seg / 2;
    const jitter = (Math.random() - 0.5) * seg * 0.6;
    const currentMod = ((rotRef.current % 360) + 360) % 360;
    let desiredMod = (((360 - center - jitter) % 360) + 360) % 360;
    let delta = desiredMod - currentMod;
    if (delta < 0) delta += 360;
    const next = rotRef.current + 360 * 6 + delta;
    rotRef.current = next;
    setSpinning(true);
    setRotation(next);
    setTimeout(() => {
      setSpinning(false);
      const winner = entrants[idx];
      setReveal(winner);
      confetti({ particleCount: 180, spread: 90, origin: { y: 0.6 }, colors: ["#25C4D8", "#EAF6F8", "#14556A"] });
      setTimeout(() => confetti({ particleCount: 120, spread: 120, origin: { y: 0.5 } }), 400);
    }, 6100);
  };

  const recordWinner = async () => {
    if (!reveal) return;
    await api.post("/giveaway/record-winner", { name: reveal });
    toast.success("Winner saved to Past Winners");
    loadWinners();
  };

  const prizeUrl = g?.prize_image_file_id ? `${API}/giveaway/image/${g.prize_image_file_id}` : null;

  if (!g) return <div className="min-h-screen bg-brand-base grid place-items-center text-brand-textMuted">Loading…</div>;

  const TABS = [["setup", "Setup"], ["wheel", "Wheel"], ["past", "Past Winners"]];

  return (
    <div className="min-h-screen bg-brand-base text-white">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <Gift size={18} className="text-brand-cyan" />
          <span className="font-display font-semibold">Giveaway</span>
        </div>
        <Link to="/admin/dashboard" className="text-brand-textSec text-sm inline-flex items-center gap-1 hover:text-white" data-testid="giveaway-back"><ArrowLeft size={14} /> Admin</Link>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-2 p-1 bg-[#0C131D] rounded-full w-fit mb-8">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`giveaway-tab-${v}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === v ? "bg-brand-cyan text-brand-base" : "text-brand-textSec hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {/* SETUP */}
        {tab === "setup" && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="giveaway-setup">
            <div className="bg-[#0C131D] border border-white/10 rounded-xl p-6 space-y-4">
              <h3 className="font-display font-semibold">Entrants</h3>
              <p className="text-brand-textMuted text-sm">Upload an Excel (.xlsx) or CSV with one column of YouTube usernames, or paste them below (up to 100).</p>
              <div className="flex flex-wrap gap-2">
                <label className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 cursor-pointer w-fit">
                  <Upload size={15} /> Upload names
                  <input type="file" accept=".xlsx,.xls,.csv,.txt" onChange={uploadNames} className="hidden" data-testid="giveaway-names-input" />
                </label>
                {realEntrants.length > 0 && realEntrants.length < 100 && fillers.length === 0 && (
                  <button onClick={autoFill} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="giveaway-autofill"><Wand2 size={15} /> Fill to 100</button>
                )}
                {fillers.length > 0 && (
                  <button onClick={removeFillers} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="giveaway-remove-fillers"><Wand2 size={15} /> Remove fillers</button>
                )}
                {g.entrants.length > 0 && (
                  <button onClick={clearEntrants} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2 hover:text-red-400" data-testid="giveaway-clear-entrants"><Eraser size={15} /> Clear list</button>
                )}
              </div>
              <div>
                <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={3} placeholder={"Paste usernames, one per line\n@fishfan123\n@guppyqueen"}
                  className="w-full bg-[#0A0F16] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-cyan" data-testid="giveaway-paste-input" />
                <button onClick={addPasted} disabled={!pasteText.trim()} className="btn-ghost mt-2 px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="giveaway-paste-add"><ClipboardPaste size={15} /> Add to list</button>
              </div>
              <p className="text-sm text-brand-textSec" data-testid="giveaway-entrant-count">
                {realEntrants.length} entrants loaded{fillers.length > 0 && <span className="text-brand-textMuted"> + {fillers.length} fillers (can't win)</span>}
              </p>
              {g.entrants.length > 0 && (
                <div className="max-h-52 overflow-y-auto text-sm text-brand-textSec border border-white/10 rounded-lg p-3 grid grid-cols-2 gap-x-4 gap-y-1">
                  {g.entrants.map((e, i) => <div key={i} className={`truncate ${fillers.includes(e) ? "text-brand-textMuted/60 italic" : ""}`}>{i + 1}. {e}</div>)}
                </div>
              )}
            </div>

            <div className="bg-[#0C131D] border border-white/10 rounded-xl p-6 space-y-4">
              <h3 className="font-display font-semibold">Prize</h3>
              <input defaultValue={g.title} onBlur={(e) => save({ title: e.target.value })} placeholder="Prize title (e.g. Fluval 407 Filter)"
                className="w-full bg-[#0A0F16] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-cyan" data-testid="giveaway-title" />
              <textarea defaultValue={g.description} onBlur={(e) => save({ description: e.target.value })} placeholder="Short description" rows={2}
                className="w-full bg-[#0A0F16] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-cyan" data-testid="giveaway-description" />
              <label className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2 cursor-pointer w-fit">
                <ImageIcon size={15} /> {prizeUrl ? "Replace image" : "Upload image"}
                <input type="file" accept="image/*" onChange={uploadPrize} className="hidden" data-testid="giveaway-prize-input" />
              </label>
              {prizeUrl && (
                <div className="space-y-2">
                  <img src={prizeUrl} alt="Prize" className="rounded-lg max-h-48 border border-white/10" data-testid="giveaway-prize-image" />
                  <button onClick={() => save({ prize_image_file_id: "" })} className="text-brand-textMuted hover:text-red-400 text-xs inline-flex items-center gap-1" data-testid="giveaway-prize-remove"><Trash2 size={12} /> Remove image</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* WHEEL */}
        {tab === "wheel" && (
          <div className="flex flex-col items-center gap-6" data-testid="giveaway-wheel-tab">
            {g.title && <h2 className="font-display font-bold text-3xl text-center">{g.title}</h2>}
            <Wheel entrants={g.entrants} rotation={rotation} spinning={spinning} />
            <button onClick={spin} disabled={spinning || g.entrants.length === 0}
              className="btn-primary px-8 py-3 text-base inline-flex items-center gap-2 disabled:opacity-50" data-testid="giveaway-spin">
              <Play size={18} /> {spinning ? "Spinning…" : "Spin the wheel"}
            </button>
            {g.entrants.length === 0 && <p className="text-brand-textMuted text-sm">Add entrants in Setup first.</p>}

            {reveal && (
              <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4" data-testid="giveaway-reveal" onClick={() => setReveal(null)}>
                <div className="bg-[#0C131D] border border-brand-cyan/40 rounded-2xl p-8 max-w-md w-full text-center" onClick={(e) => e.stopPropagation()}>
                  <Trophy size={40} className="text-brand-cyan mx-auto" />
                  <p className="text-brand-textSec mt-3 uppercase tracking-widest text-xs">Winner</p>
                  <p className="font-display font-bold text-4xl mt-1 break-words" data-testid="giveaway-winner-name">{reveal}</p>
                  {g.title && <p className="text-brand-cyan mt-2">{g.title}</p>}
                  {prizeUrl && <img src={prizeUrl} alt="Prize" className="rounded-lg max-h-52 mx-auto mt-4 border border-white/10" />}
                  <div className="flex flex-wrap gap-3 justify-center mt-6">
                    <button onClick={recordWinner} className="btn-primary px-5 py-2.5 text-sm" data-testid="giveaway-save-winner">Save winner</button>
                    <button onClick={() => setCard({ name: reveal, prize: g.title, date: new Date().toISOString() })} className="btn-ghost px-5 py-2.5 text-sm inline-flex items-center gap-2" data-testid="giveaway-reveal-card"><ImageDown size={15} /> Winner card</button>
                    <button onClick={() => setReveal(null)} className="btn-ghost px-5 py-2.5 text-sm" data-testid="giveaway-close">Close</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PAST WINNERS */}
        {tab === "past" && (
          <div data-testid="giveaway-past-tab">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold">Past winners</h3>
              <button onClick={async () => { const d = await save({ winners_public: !g.winners_public }); toast.success(d.winners_public ? "Now public" : "Now private"); }}
                className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="giveaway-visibility-toggle">
                {g.winners_public ? <><Eye size={15} /> Public</> : <><EyeOff size={15} /> Private</>}
              </button>
            </div>
            <div className="border border-white/10 rounded-lg overflow-hidden">
              {winners.length === 0 && <p className="px-4 py-8 text-center text-brand-textMuted text-sm">No winners saved yet.</p>}
              {winners.map((w) => (
                <div key={w.id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0" data-testid={`giveaway-winner-row-${w.id}`}>
                  <div className="flex items-center gap-3">
                    {w.prize_image_file_id && <img src={`${API}/giveaway/image/${w.prize_image_file_id}`} alt="" className="h-10 w-10 rounded object-cover border border-white/10" />}
                    <div>
                      <p className="font-medium">{w.name}</p>
                      <p className="text-brand-textMuted text-xs">{w.prize_title} · {String(w.date).slice(0, 10)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setCard({ name: w.name, prize: w.prize_title, date: w.date })} className="text-brand-textMuted hover:text-brand-cyan" title="Winner card" data-testid={`giveaway-winner-card-${w.id}`}><ImageDown size={16} /></button>
                    <button onClick={async () => { await api.delete(`/giveaway/winners/${w.id}`); loadWinners(); }} className="text-brand-textMuted hover:text-red-400" data-testid={`giveaway-winner-delete-${w.id}`}><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {card && <WinnerCardModal {...card} onClose={() => setCard(null)} />}
    </div>
  );
}
